import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@bonusly/design-system';

import { defaultStyle, getStyleFromName } from '../../design-system/avatar/utils/style';

interface Bubble {
	x: number; // percentage, represents the center of the bubble
	y: number; // percentage, represents the center of the bubble
	size: number; // percentage of container dimension
}

function mulberry32(seed: number) {
	let s = seed;
	return () => {
		s = (s + 0x6d2b79f5) | 0;
		let t = Math.imul(s ^ (s >>> 15), 1 | s);
		t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
}

function hashString(str: string): number {
	let h = 0;
	for (let i = 0; i < str.length; i++) {
		h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
	}
	return Math.abs(h);
}

function shuffled<T>(arr: T[], rand: () => number): T[] {
	const out = [...arr];
	for (let i = out.length - 1; i > 0; i--) {
		const j = Math.floor(rand() * (i + 1));
		[out[i], out[j]] = [out[j], out[i]];
	}
	return out;
}

// Two-pass bubble generation:
//
// Pass 1 — background fill: 6-column grid, large bubbles (22–30% of container
// width). Row count scales with heightToWidthRatio so each bubble is ~1.5×
// the cell height, guaranteeing full coverage regardless of container shape.
//
// Pass 2 — accent layer: 7-column grid, ~40% of cells randomly selected,
// smaller bubbles (8–14%). Rendered on top of pass 1 for visual variety.
//
// Both passes are shuffled before rendering so z-stacking order is random.
function generateBubbles(seed: string, heightToWidthRatio: number = 427 / 620): Bubble[] {
	const rand = mulberry32(hashString(seed));

	// --- Pass 1: background fill ---
	const bgCols = 6;
	const bgRows = Math.max(bgCols, Math.ceil(bgCols * heightToWidthRatio));
	const bgStepX = 100 / bgCols;
	const bgStepY = 100 / bgRows;
	const bgCells = shuffled(
		Array.from({ length: bgCols * bgRows }, (_, i) => ({ row: Math.floor(i / bgCols), col: i % bgCols })),
		rand
	);
	const bgBubbles: Bubble[] = bgCells.map(({ row, col }) => ({
		x: (col + 0.5) * bgStepX + (rand() - 0.5) * bgStepX * 0.6,
		y: (row + 0.5) * bgStepY + (rand() - 0.5) * bgStepY * 0.6,
		size: 22 + rand() * 8,
	}));

	// --- Pass 2: accent layer ---
	const fgCols = 7;
	const fgRows = Math.max(fgCols, Math.ceil(fgCols * heightToWidthRatio));
	const fgStepX = 100 / fgCols;
	const fgStepY = 100 / fgRows;
	const fgCells = shuffled(
		Array.from({ length: fgCols * fgRows }, (_, i) => ({ row: Math.floor(i / fgCols), col: i % fgCols })).filter(() => rand() < 0.4),
		rand
	);
	const fgBubbles: Bubble[] = fgCells.map(({ row, col }) => ({
		x: (col + 0.5) * fgStepX + (rand() - 0.5) * fgStepX,
		y: (row + 0.5) * fgStepY + (rand() - 0.5) * fgStepY,
		size: 10 + rand() * 6,
	}));

	return [...bgBubbles, ...fgBubbles];
}

interface AvatarRecipient {
	id: string;
	src: string;
	name?: string;
}

interface AvatarBubbleWrapProps {
	name?: string;
	src?: string;
	recipients?: AvatarRecipient[];
	/** Seed string for deterministic bubble layout — use bonus ID in the feed, a stable key in the gallery */
	seed?: string;
	/** Width of the white border ring around each bubble. Use a smaller value for thumbnails. */
	borderWidth?: string;
}

export const AvatarBubbleWrap: FC<AvatarBubbleWrapProps> = ({ name, src, recipients, seed = 'default', borderWidth = '2px' }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [heightToWidthRatio, setHeightToWidthRatio] = useState(427 / 620);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const observer = new ResizeObserver((entries) => {
			const { width, height } = entries[0].contentRect;
			if (width > 0) setHeightToWidthRatio(height / width);
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const bubbles = useMemo(() => generateBubbles(seed, heightToWidthRatio), [seed, heightToWidthRatio]);

	const sortedRecipients = useMemo(() => {
		if (!recipients) return undefined;
		return [...recipients].sort((a, b) => a.id.localeCompare(b.id));
	}, [recipients]);

	return (
		<Box ref={containerRef} position="relative" w="100%" h="100%" bg="white" overflow="hidden">
			{bubbles.map((bubble, i) => {
				const avatarSrc = sortedRecipients ? sortedRecipients[i % sortedRecipients.length].src : src;
				const avatarName = sortedRecipients ? (sortedRecipients[i % sortedRecipients.length].name ?? '') : (name ?? '');
				const style = avatarName ? getStyleFromName(avatarName) : defaultStyle;
				const initials = avatarName
					.split(' ')
					.map((w) => w[0])
					.filter(Boolean)
					.slice(0, 2)
					.join('')
					.toUpperCase();

				return (
					<Box
						key={`${seed}-${i}`}
						position="absolute"
						left={`${bubble.x}%`}
						top={`${bubble.y}%`}
						transform="translate(-50%, -50%)"
						width={`${bubble.size}%`}
						aspectRatio="1"
						borderRadius="full"
						overflow="hidden"
						boxShadow={`0 0 0 ${borderWidth} white`}
						aria-hidden
						sx={{ containerType: 'inline-size' }}
					>
						{avatarSrc ? (
							<Box as="img" src={avatarSrc} alt="" w="100%" h="100%" objectFit="cover" display="block" />
						) : (
							<Box
								w="100%"
								h="100%"
								display="flex"
								alignItems="center"
								justifyContent="center"
								bg={style.bg}
								color={style.color}
								fontSize="35cqw"
								fontWeight="medium"
								lineHeight="1"
							>
								{initials}
							</Box>
						)}
					</Box>
				);
			})}
		</Box>
	);
};
